/**
 * Vertical drag-to-reorder for the Pinned section — pointer events, so ONE
 * implementation serves mouse and touch alike (the product ships to every
 * surface; owner, 2026-08-25: 不管手機還是 webapp 都需要通盤考慮).
 *
 * The gesture lives on a dedicated HANDLE, not the row: rows already carry a
 * click (open the agent) and an HTML5 drag (onto the stage, 2026-08-22), and a
 * third meaning on the same surface would fight both. The handle declares
 * touch-action: none (shell.css), which is what stops a phone from scrolling
 * the rail instead of moving the row.
 *
 * Geometry over drop-targets: rects are measured ONCE at pointerdown (the list
 * is not scrolling mid-drag — the pointer is captured), the target index is
 * pure arithmetic (pinOrder.targetIndex), and rows between shift by one row
 * height via transform so the drop position is visible the whole way.
 */
import { useRef, useState } from 'react'
import { targetIndex } from './pinOrder'

export interface PinnedDrag {
  /** Index being dragged, or null. */
  from: number | null
  /** Where it would land if released now. */
  to: number | null
  /** The dragged row's current translateY, px. */
  delta: number
  /** Row height measured at drag start — what the others shift by. */
  rowH: number
  /** Attach to the handle of row `i`. */
  handleProps: (i: number) => {
    onPointerDown: (e: React.PointerEvent<HTMLElement>) => void
  }
}

export function usePinnedDrag(
  count: number,
  containerRef: React.RefObject<HTMLElement | null>,
  onMove: (from: number, to: number) => void,
): PinnedDrag {
  const [from, setFrom] = useState<number | null>(null)
  const [to, setTo] = useState<number | null>(null)
  const [delta, setDelta] = useState(0)
  const geom = useRef<{ tops: number[]; h: number; startY: number }>({ tops: [], h: 0, startY: 0 })

  const handleProps = (i: number) => ({
    onPointerDown: (e: React.PointerEvent<HTMLElement>) => {
      // Primary button / first touch only; a right-click is a menu, not a drag.
      if (e.button !== 0 && e.pointerType === 'mouse') return
      const rows = Array.from(
        containerRef.current?.querySelectorAll<HTMLElement>('[data-pin-row]') ?? [],
      )
      if (rows.length !== count || !rows[i]) return
      const tops = rows.map((r) => r.offsetTop)
      geom.current = { tops, h: rows[i]!.offsetHeight, startY: e.clientY }
      setFrom(i)
      setTo(i)
      setDelta(0)
      const target = e.currentTarget
      target.setPointerCapture?.(e.pointerId)
      e.preventDefault()
      e.stopPropagation()

      const move = (ev: PointerEvent) => {
        const d = ev.clientY - geom.current.startY
        setDelta(d)
        setTo(targetIndex(geom.current.tops, geom.current.h, i, d))
      }
      const up = (ev: PointerEvent) => {
        target.removeEventListener('pointermove', move)
        target.removeEventListener('pointerup', up)
        target.removeEventListener('pointercancel', up)
        const d = ev.clientY - geom.current.startY
        const landed = targetIndex(geom.current.tops, geom.current.h, i, d)
        setFrom(null)
        setTo(null)
        setDelta(0)
        if (ev.type === 'pointerup' && landed !== i) onMove(i, landed)
      }
      target.addEventListener('pointermove', move)
      target.addEventListener('pointerup', up)
      target.addEventListener('pointercancel', up)
    },
  })

  return { from, to, delta, rowH: geom.current.h, handleProps }
}
